import { useMemo } from 'react'

import type { ColumnSort } from '@tanstack/table-core'

import { sendGTMEvent } from '@b-stock/fe-core/bstock-next/components'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'

import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

// Disabled pdp_interaction dataLayer events for these interaction types
// (Segment tracking via trackButtonClicked is unaffected).
const DISABLED_PDP_INTERACTION_TYPES = new Set([
  'manifest_scroll_depth',
  'manifest_tooltip',
])

function sendPdpInteraction(
  listing: OrchestratedListingDetails,
  interactionType: string,
  detail?: string
) {
  if (DISABLED_PDP_INTERACTION_TYPES.has(interactionType)) return

  sendGTMEvent({
    event: 'pdp_interaction',
    platform: GTM_PLATFORM,
    interaction_type: interactionType,
    item_id: listing._id,
    auctionId: listing.auction?._id,
    listingId: listing._id,
    page_type: 'pdp',
    ...(detail && { interaction_detail: detail }),
  })
}

// Module-scoped guard so manifest_table_engaged fires once per page view across
// the components that each call the hook. useManifestTracking clears this on
// the router's routeChangeStart event so a fresh page view re-engages.
let manifestEngaged = false

export const resetManifestEngaged = () => {
  manifestEngaged = false
}

const NOOP_CALLBACKS = {
  onDownloadFullManifest: () => {},
  onManifestCellCopied: (
    _columns: string[],
    _surface: 'inline' | 'fullscreen',
    _way: 'context_menu' | 'keyboard'
  ) => {},
  onManifestColumnScrolled: () => {},
  onManifestColumnsChanged: () => {},
  onManifestColumnsReset: () => {},
  onManifestFiltersApplied: () => {},
  onManifestFiltersButtonClicked: () => {},
  onManifestFilterReset: () => {},
  onManifestFullscreenClosed: () => {},
  onManifestFullscreenEngaged: () => {},
  onManifestFullscreenOpened: () => {},
  onManifestFullscreenScrolled: () => {},
  onManifestFullyViewed: (_surface: 'inline' | 'fullscreen') => {},
  onManifestMobileScrollPastManifest: () => {},
  onManifestPageChanged: () => {},
  onManifestReset: () => {},
  onManifestScrollDepth: (
    _surface: 'inline' | 'fullscreen',
    _depthPercent: number
  ) => {},
  onManifestScrollTracking: () => {},
  onManifestSearch: () => {},
  onManifestSearchClear: () => {},
  onManifestTabChanged: () => {},
  onManifestTableEngaged: () => {},
  onManifestTableViewed: () => {},
  onManifestTooltip: () => {},
  onSortingChanged: () => {},
}

export const useManifestTableTracking = (
  listing: OrchestratedListingDetails | null
) => {
  const { trackButtonClicked } = useAnalytics()

  return useMemo(() => {
    if (!listing) return NOOP_CALLBACKS

    return {
      onManifestCellCopied: (
        columns: string[],
        surface: 'inline' | 'fullscreen',
        way: 'context_menu' | 'keyboard'
      ) => {
        trackButtonClicked({
          buttonName: 'manifest_cell_copied',
          listing_id: listing.lot._id,
          columns,
          surface,
          way,
        })
      },

      onManifestColumnsChanged: (colId: string, visible: boolean) => {
        trackButtonClicked({
          buttonName: 'manifest_column_visibility',
          column: colId,
          is_visible: visible,
        })
        sendPdpInteraction(
          listing,
          'manifest_column_visibility',
          `${colId}:${visible ? 'show' : 'hide'}`
        )
      },

      onManifestColumnsReset: () => {
        trackButtonClicked({
          buttonName: 'manifest_column_visibility_reset',
        })
        sendPdpInteraction(listing, 'manifest_column_visibility_reset')
      },

      onDownloadFullManifest: () => {
        trackButtonClicked({
          buttonName: 'download_full_manifest',
          listing_id: listing.lot._id,
          seller_account_id: listing.accountId,
          seller_display_name: listing.seller.account.displayName,
          storefront_name: listing.seller.storefront?.name,
        })
        sendPdpInteraction(listing, 'manifest_download')
      },

      onManifestFiltersApplied: (data: {
        filterTitle: string | string[]
        filterName: string | string[]
        isSelected: boolean
      }) => {
        trackButtonClicked({
          buttonName: 'manifest_filters',
          filter_title: data.filterTitle,
          filter_name: data.filterName,
          is_selected: data.isSelected,
        })
        sendPdpInteraction(listing, 'manifest_filter', String(data.filterTitle))
      },

      onManifestFiltersButtonClicked: () => {
        trackButtonClicked({
          buttonName: 'manifest_filters',
          filter_title: 'filter_button_manifest',
        })
      },

      onManifestFilterReset: (data: {
        filterTitle: string
        filterKey: string
      }) => {
        trackButtonClicked({
          buttonName: 'reset_manifest_filter',
          filter_title: data.filterTitle,
          filter_key: data.filterKey,
          is_selected: false,
        })
      },

      onManifestPageChanged: (pageNumber: number) => {
        trackButtonClicked({
          buttonName: 'manifest_page_changed',
          listing_id: listing.lot._id,
          page_number: pageNumber,
        })
        sendPdpInteraction(listing, 'manifest_page_changed', String(pageNumber))
      },

      onManifestReset: () => {
        trackButtonClicked({
          buttonName: 'reset_manifest',
        })
      },

      onManifestScrollTracking: (data: {
        axis: 'horizontal' | 'vertical'
        direction: 'up' | 'down' | 'left' | 'right'
        total_rows: number
        total_cols: number
      }) => {
        trackButtonClicked({
          buttonName: 'scrolling_within_manifest',
          total_rows: data.total_rows,
          total_cols: data.total_cols,
          first_scroll_axis: data.axis,
          first_scroll_direction: data.direction,
        })
      },

      onManifestSearch: (searchTerm: string) => {
        trackButtonClicked({
          buttonName: 'manifest_filters',
          filter_title: 'search_manifest',
          search_term: searchTerm,
        })
        sendPdpInteraction(listing, 'manifest_search', searchTerm)
      },

      onManifestSearchClear: () => {
        trackButtonClicked({
          buttonName: 'clear_manifest_search',
        })
      },

      onManifestTabChanged: (tabKey: string) => {
        trackButtonClicked({
          buttonName: 'manifest_header',
          header_section: tabKey.toLowerCase(),
          listing_id: listing.lot._id,
          seller_account_id: listing.accountId,
          seller_display_name: listing.seller.account.displayName,
          storefront_name: listing.seller.storefront?.name,
        })
        sendPdpInteraction(listing, 'manifest_view', tabKey.toLowerCase())
      },

      onManifestTooltip: (
        colId: string | null | undefined,
        rowIndex: number | undefined
      ) => {
        trackButtonClicked({
          buttonName: 'manifest_tooltip',
          column: colId,
          row_index: rowIndex,
        })
        sendPdpInteraction(listing, 'manifest_tooltip', colId ?? undefined)
      },

      onManifestTableEngaged: () => {
        if (manifestEngaged) return
        manifestEngaged = true
        trackButtonClicked({
          buttonName: 'manifest_table_engaged',
          listing_id: listing.lot._id,
        })
        sendPdpInteraction(listing, 'manifest_table_engaged')
      },

      onManifestTableViewed: () => {
        trackButtonClicked({
          buttonName: 'manifest_table_viewed',
          listing_id: listing.lot._id,
          listing_category:
            listing.lot.categories?.[0]?.categories?.[0] ?? 'unknown',
        })
        sendPdpInteraction(listing, 'manifest_table_viewed')
      },

      onManifestColumnScrolled: (depth: number) => {
        trackButtonClicked({
          buttonName: 'manifest_column_scrolled',
          listing_id: listing.lot._id,
          scroll_depth_columns: depth,
        })
        sendPdpInteraction(listing, 'manifest_column_scrolled', String(depth))
      },

      onManifestFullscreenOpened: () => {
        trackButtonClicked({
          buttonName: 'manifest_fullscreen_opened',
          listing_id: listing.lot._id,
          listing_category:
            listing.lot.categories?.[0]?.categories?.[0] ?? 'unknown',
        })
        sendPdpInteraction(listing, 'manifest_fullscreen_opened')
      },

      onManifestFullscreenClosed: (timeInFullscreenSeconds: number) => {
        trackButtonClicked({
          buttonName: 'manifest_fullscreen_closed',
          listing_id: listing.lot._id,
          time_in_fullscreen_seconds: timeInFullscreenSeconds,
        })
        sendPdpInteraction(listing, 'manifest_fullscreen_closed')
      },

      onManifestFullscreenEngaged: () => {
        trackButtonClicked({
          buttonName: 'manifest_fullscreen_engaged',
          listing_id: listing.lot._id,
        })
        sendPdpInteraction(listing, 'manifest_fullscreen_engaged')
      },

      onManifestFullscreenScrolled: (
        scrollDepthRows: number,
        scrollDepthColumns: number
      ) => {
        trackButtonClicked({
          buttonName: 'manifest_fullscreen_scrolled',
          listing_id: listing.lot._id,
          scroll_depth_rows: scrollDepthRows,
          scroll_depth_columns: scrollDepthColumns,
        })
        sendPdpInteraction(
          listing,
          'manifest_fullscreen_scrolled',
          `${scrollDepthRows}:${scrollDepthColumns}`
        )
      },

      // [BP-2378] scroll-depth/fully-viewed as a percentage of the total
      // manifest, distinct from onManifestColumnScrolled/
      // onManifestFullscreenScrolled above (which count rows/columns crossed
      // during pixel-scroll, not position relative to the full list).
      onManifestScrollDepth: (
        surface: 'inline' | 'fullscreen',
        depthPercent: number
      ) => {
        trackButtonClicked({
          buttonName: 'manifest_scroll_depth',
          listing_id: listing.lot._id,
          surface,
          scroll_depth_percent: depthPercent,
        })
        sendPdpInteraction(
          listing,
          'manifest_scroll_depth',
          `${surface}:${depthPercent}`
        )
      },

      onManifestFullyViewed: (surface: 'inline' | 'fullscreen') => {
        trackButtonClicked({
          buttonName: 'manifest_fully_viewed',
          listing_id: listing.lot._id,
          surface,
        })
        sendPdpInteraction(listing, 'manifest_fully_viewed', surface)
      },

      onManifestMobileScrollPastManifest: () => {
        trackButtonClicked({
          buttonName: 'mobile_scroll_past_manifest',
          listing_id: listing.lot._id,
        })
        sendPdpInteraction(listing, 'mobile_scroll_past_manifest')
      },

      onSortingChanged: (sorting: ColumnSort[] | undefined) => {
        if (sorting && sorting.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- length check guarantees element exists
          const primarySort = sorting[0]!
          trackButtonClicked({
            buttonName: 'manifest_sort',
            sort_column: primarySort.id,
            sort_direction: primarySort.desc ? 'desc' : 'asc',
          })
          sendPdpInteraction(
            listing,
            'manifest_sort',
            `${primarySort.id}:${primarySort.desc ? 'desc' : 'asc'}`
          )
        }
      },
    }
  }, [listing, trackButtonClicked])
}
